import type { FC } from 'react';
import { InlineEditableText } from '@components/InlineEditableText';
import InputSelect from '@components/InputSelect';
import { responsiveTruncateClasses } from '@ContractBuilder/contract-builder-header/classes';
import { EntityField } from '@ContractBuilder/contract-builder-header/constants';
import { useDatapointsStore } from '@ContractBuilder/store/datapoints.store';
import type { EntityData } from '@ContractBuilder/types';
import { filterPublishedTemplates } from '@features/create-entity-modal/views/template/Step2';
import { templateQueries } from '@queries/fetchTemplates';
import type { UIInputValue, UISelectionType } from '@root/@types/types';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';

import { useEditableField } from '../hooks';

interface EditableTemplateBaseFieldProps {
  className?: string;
  entity: EntityData;
  hasOperationsDisabled: boolean;
  onSave: (id: string, cb: () => void) => Promise<void>;
}

const hasTemplateAsBaseResource = (entity: EntityData) => entity.sourceResourceName === 'templates';

export const EditableTemplateBaseField: FC<EditableTemplateBaseFieldProps> = ({
  className,
  entity,
  hasOperationsDisabled,
  onSave,
}) => {
  const hasUnsavedChanges = useDatapointsStore((state) => state.isDirty);
  const isDisabled = hasOperationsDisabled || hasUnsavedChanges;

  const { onCancel, onChange, onClick, onUpdate, state } = useEditableField<string | undefined>({
    defaultValue: hasTemplateAsBaseResource(entity) ? entity.sourceResourceId : undefined,
    exitEditModeAfterConfirm: false,
    name: EntityField.TemplateBase,
    onSave: (_name, value, onCancel) => onSave(value as string, onCancel),
  });

  const { data = [], isLoading } = useQuery({
    ...templateQueries.list(),
    enabled: state.isEditing ?? false,
  });

  if (isLoading) {
    return <div className="flex h-4 w-[120px] animate-pulse rounded-xl bg-slate-200" />;
  }

  const options = data.filter(filterPublishedTemplates).map(({ id, name }) => ({ value: id, name }) as UISelectionType);

  return (
    <div className={clsx('flex items-center gap-1', className)}>
      <InlineEditableText
        editor={
          <InputSelect
            dataTestId="select:editable-template-base"
            formatter={(item) => {
              const template = data.find((t) => t.id === item);
              return template?.name ?? entity.sourceResourceDisplayName;
            }}
            isFetchingOptions={isLoading}
            menuOuterClassName="min-w-28"
            name="sourceResourceId"
            onChange={(value) => onChange(value as string)}
            options={options}
            size="sm"
            value={state.value as UIInputValue}
          />
        }
        editMode={state.isEditing}
        disabled={isDisabled}
        display={entity.sourceResourceDisplayName}
        confirmDisabled={!state.value || hasUnsavedChanges}
        onClick={onClick}
        onCancel={onCancel}
        onConfirm={onUpdate}
        tooltipContent={!isDisabled ? 'Change base template' : undefined}
        withEditIcon={false}
        className={responsiveTruncateClasses([
          'rounded-sm border border-transparent px-1 py-0.5',
          !isDisabled ? 'hover:border-gray-300' : '',
        ])}
      />
    </div>
  );
};
